import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { Link } from 'react-router';
import {
  sharedRateScrapeMetadataGetOptions,
  sharedRateScrapeMetadataActionPropagateMutation,
  sharedRateScrapeMetadataSaveMutation,
} from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type { SelectorSetBean } from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import type {
  SharedRateScrapeMetadataBean,
  SharedRateScrapeMetadataBeanWritable,
} from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import type { EditableFieldSpec, EditableFieldValue, EntityDetailRow } from './EditableEntityTable';
import { EditableEntityTable } from './EditableEntityTable';
import { ConfirmModal } from './ConfirmModal';
import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import {
  getApiErrorMessage,
  getProblemDetailMessage,
} from '@quoruum/frontend-common/api/errorMessage';
import type { BookingEngineType } from '../constants/bookingEngines';

type EditableSharedMetadataFieldKey = 'selectorSets' | 'indicatorSets';

type SharedScrapeMetadataPanelProps = {
  bookingEngineType: BookingEngineType;
  tableKeyPrefix?: string;
};

const SHARED_METADATA_EDITABLE_KEYS = new Set<EditableSharedMetadataFieldKey>([
  'selectorSets',
  'indicatorSets',
]);

const SHARED_METADATA_EDITABLE_FIELD_SPECS: ReadonlyMap<
  EditableSharedMetadataFieldKey,
  EditableFieldSpec
> = new Map(
  Array.from(SHARED_METADATA_EDITABLE_KEYS).map((key) => [
    key,
    { editType: 'textarea', heightPx: 180 },
  ])
);

function toPrettyJson(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  return JSON.stringify(value, null, 2);
}

function parseJsonObjectDraft(value: string): Record<string, unknown> | Record<string, unknown>[] {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return {};
  }

  let parsedValue: unknown;
  try {
    parsedValue = JSON.parse(trimmedValue);
  } catch {
    throw new Error('Please enter valid JSON.');
  }

  if (typeof parsedValue !== 'object' || parsedValue === null) {
    throw new Error('Please enter a JSON object or array.');
  }

  return parsedValue as Record<string, unknown> | Record<string, unknown>[];
}

function formatUnknownValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'string') {
    return value || '-';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (
    Array.isArray(value) &&
    value.every(
      (item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
    )
  ) {
    if (value.length === 0) return '-';
    return (
      <ul className="list-none space-y-0.5">
        {value.map((item, index) => (
          <li key={index} className="text-xs text-slate-800">
            {String(item)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <SyntaxHighlighter
      language="json"
      customStyle={{
        margin: 0,
        padding: '0.5rem',
        borderRadius: '0.375rem',
        fontSize: '0.7rem',
        lineHeight: 1.4,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowX: 'scroll',
      }}
      wrapLongLines
    >
      {JSON.stringify(value, null, 2)}
    </SyntaxHighlighter>
  );
}

export function buildHotelMapHref(hotelId: string): string {
  const searchParams = new URLSearchParams();
  searchParams.set('selectedId', hotelId);
  searchParams.set('searchMode', 'HOTEL');
  searchParams.set('drawerTab', 'HOTEL');

  return `/tools/hotel-explorer?${searchParams.toString()}`;
}

function buildSharedMetadataRows(
  metadata: SharedRateScrapeMetadataBean
): Array<EntityDetailRow<EditableSharedMetadataFieldKey>> {
  const metadataEntries = Object.entries(metadata as Record<string, unknown>);

  return metadataEntries.map(([key, value]) => {
    if (key === 'bootstrapHotelIds' && Array.isArray(value)) {
      const bootstrapHotelIds = value.filter(
        (item): item is string => typeof item === 'string' && item.trim().length > 0
      );

      return {
        key,
        value:
          bootstrapHotelIds.length === 0 ? (
            '-'
          ) : (
            <ul className="list-none space-y-0.5">
              {bootstrapHotelIds.map((hotelId) => (
                <li key={hotelId} className="text-xs text-slate-800">
                  <Link
                    to={buildHotelMapHref(hotelId)}
                    className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
                  >
                    {hotelId}
                  </Link>
                </li>
              ))}
            </ul>
          ),
      };
    }

    if (SHARED_METADATA_EDITABLE_KEYS.has(key as EditableSharedMetadataFieldKey)) {
      return {
        key,
        value: formatUnknownValue(value),
        editableFieldKey: key as EditableSharedMetadataFieldKey,
        editableFieldValue: toPrettyJson(value),
      };
    }

    return {
      key,
      value: formatUnknownValue(value),
    };
  });
}

export function SharedScrapeMetadataPanel({
  bookingEngineType,
  tableKeyPrefix = '',
}: SharedScrapeMetadataPanelProps): ReactElement {
  const sharedMetadataOptions = sharedRateScrapeMetadataGetOptions({
    path: { bookingEngine: bookingEngineType },
  });

  const sharedMetadataQuery = useQuery(sharedMetadataOptions);
  const saveSharedMetadataMutation = useMutation(sharedRateScrapeMetadataSaveMutation());
  const propagateSharedMetadataMutation = useMutation(
    sharedRateScrapeMetadataActionPropagateMutation()
  );

  const [isPropagateConfirmModalOpen, setIsPropagateConfirmModalOpen] = useState(false);
  const [propagateSharedMetadataError, setPropagateSharedMetadataError] = useState<string | null>(
    null
  );

  const rows = useMemo(() => {
    if (!sharedMetadataQuery.data) {
      return [];
    }

    return buildSharedMetadataRows(sharedMetadataQuery.data);
  }, [sharedMetadataQuery.data]);

  async function handleSaveSharedMetadataField(
    fieldKey: EditableSharedMetadataFieldKey,
    value: EditableFieldValue
  ): Promise<void> {
    if (typeof value !== 'string') {
      throw new Error('Invalid value for selected field.');
    }

    const currentMetadata = sharedMetadataQuery.data;
    if (!currentMetadata) {
      throw new Error('Unable to save field. Shared metadata is not loaded.');
    }

    const nextMetadataValue = parseJsonObjectDraft(value);

    const updateSelectorMetadata = (): SelectorSetBean[] => {
      return nextMetadataValue as SelectorSetBean[];
    };

    const body: SharedRateScrapeMetadataBeanWritable = {
      ...currentMetadata,
      bookingEngine: bookingEngineType,
      selectorSets:
        fieldKey === 'selectorSets' ? updateSelectorMetadata() : currentMetadata.selectorSets,
      indicatorSets:
        fieldKey === 'indicatorSets' ? updateSelectorMetadata() : currentMetadata.indicatorSets,
    };

    try {
      await saveSharedMetadataMutation.mutateAsync({
        path: { bookingEngine: bookingEngineType },
        body,
      });
      await queryClient.query(sharedMetadataOptions);
    } catch (error) {
      let message = 'Unable to save field.';
      const detail = getProblemDetailMessage(error);
      if (detail) {
        message = `Unable to save field. ${detail}`;
      }
      const wrappedError = new Error(message) as Error & { cause?: unknown };
      wrappedError.cause = error;
      throw wrappedError;
    }
  }

  async function handleConfirmPropagateSharedMetadata(): Promise<void> {
    setPropagateSharedMetadataError(null);

    try {
      await propagateSharedMetadataMutation.mutateAsync({
        path: { bookingEngine: bookingEngineType },
      });
      setIsPropagateConfirmModalOpen(false);
    } catch (error) {
      let message = 'Unable to propagate shared scrape metadata.';
      if (
        typeof error === 'object' &&
        error !== null &&
        'detail' in error &&
        typeof (error as Record<string, unknown>).detail === 'string'
      ) {
        message = `Unable to propagate shared scrape metadata. ${(error as Record<string, string>).detail}`;
      }
      setPropagateSharedMetadataError(message);
    }
  }

  if (sharedMetadataQuery.isLoading) {
    return (
      <p className="px-3 py-2 text-xs text-slate-600">
        Loading shared scrape metadata for {bookingEngineType}...
      </p>
    );
  }

  if (sharedMetadataQuery.isError) {
    return (
      <p className="px-3 py-2 text-xs text-rose-700">
        {getApiErrorMessage(
          sharedMetadataQuery.error,
          `Unable to load shared scrape metadata for ${bookingEngineType}.`
        )}
      </p>
    );
  }

  return (
    <>
      <div className="flex items-center justify-end border-b border-slate-200 px-3 py-3">
        <button
          type="button"
          onClick={() => {
            setPropagateSharedMetadataError(null);
            setIsPropagateConfirmModalOpen(true);
          }}
          disabled={propagateSharedMetadataMutation.isPending}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {propagateSharedMetadataMutation.isPending ? 'Propagating...' : 'Propagate'}
        </button>
      </div>
      <EditableEntityTable
        key={`${tableKeyPrefix}${bookingEngineType}`}
        rows={rows}
        fieldDefinitions={SHARED_METADATA_EDITABLE_FIELD_SPECS}
        onSave={handleSaveSharedMetadataField}
      />
      <ConfirmModal
        isOpen={isPropagateConfirmModalOpen}
        onClose={() => {
          if (propagateSharedMetadataMutation.isPending) {
            return;
          }
          setIsPropagateConfirmModalOpen(false);
          setPropagateSharedMetadataError(null);
        }}
        onSubmit={() => {
          void handleConfirmPropagateSharedMetadata();
        }}
        title="Propagate Shared Scrape Metadata"
        message="Propagate this Shared Scrape Metadata to all hotels in the company of the same booking engine"
        ariaLabel="Propagate shared scrape metadata confirmation"
        isSubmitting={propagateSharedMetadataMutation.isPending}
        isSubmitDisabled={propagateSharedMetadataMutation.isPending}
        submitLabel="Confirm"
        submittingLabel="Propagating..."
        errorMessage={propagateSharedMetadataError}
      />
    </>
  );
}
